/**
 * Stone Investment Area Hero
 * 
 * Cinematic hero section for area/neighbourhood pages with Stone Investment's luxury aesthetic:
 * - Dark map background showing the area location
 * - Editorial typography
 * - Region-aware flag colours
 */

'use client';

import Image from 'next/image';
import { StoneAreaMapThumbnail } from './StoneAreaMapThumbnail';
import { detectRegion } from '../utils/regionColors';

export interface StoneAreaHeroProps {
  area: {
    name: string;
    region?: string;
    heroImage?: string;
    coordinates?: string;
    latitude?: number;
    longitude?: number;
  };
  className?: string;
}

export function StoneAreaHero({
  area,
  className = '',
}: StoneAreaHeroProps) {
  const region = detectRegion(area.region || area.name);
  const isFrance = region === 'provence';
  
  return (
    <section className={`relative isolate h-[70vh] min-h-[500px] max-h-[800px] overflow-hidden ${className}`}>
      {/* Background: property photo, dark map, or branded gradient */}
      {area.heroImage ? (
        <Image
          src={area.heroImage}
          alt={area.name}
          fill
          className="object-cover"
          priority
          sizes="100vw"
        />
      ) : area.latitude && area.longitude ? (
        <StoneAreaMapThumbnail
          latitude={area.latitude}
          longitude={area.longitude}
          zoom={14}
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-stone-900 via-stone-800 to-stone-700" />
      )}
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-stone-900/80 via-stone-900/30 to-stone-900/10" />
      
      {/* Content */}
      <div className="absolute inset-0 flex items-end">
        <div className="container mx-auto max-w-7xl px-6 md:px-12 pb-16 md:pb-24">
          {/* Region Badge */}
          {area.region && (
            <div className="inline-block mb-6">
              <span className="text-[10px] uppercase tracking-[0.3em] text-stone-300 font-sans border border-stone-500/50 px-4 py-2">
                {area.region}
              </span>
            </div>
          )}
          
          {/* Title */}
          <h1 
            className="font-serif text-5xl md:text-7xl lg:text-8xl text-white leading-[0.95] mb-4"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            {area.name}
          </h1>
          
          {/* Coordinates */}
          {area.coordinates && (
            <p className="font-serif text-xl md:text-2xl text-stone-300 italic">
              {area.coordinates}
            </p>
          )}
        </div>
      </div>
      
      {/* Flag Colour Strip at Bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-1 flex z-20">
        {isFrance ? (
          <>
            <div className="h-full flex-1 bg-[#002654]" />
            <div className="h-full flex-1 bg-white" />
            <div className="h-full flex-1 bg-[#ED2939]" />
          </>
        ) : (
          <>
            <div className="h-full flex-1 bg-[#EA2839]" />
            <div className="h-full flex-1 bg-[#1A206D]" />
            <div className="h-full flex-1 bg-[#FFD500]" />
            <div className="h-full flex-1 bg-[#00A551]" />
          </>
        )}
      </div>
    </section>
  );
}

export default StoneAreaHero;
